import { BoxType, ImageFrameRole } from '@lite-workspace/dto-fotoclass-templates';
import type { CustomImageTemplateBox, ImageBoxParams } from '@lite-workspace/dto-fotoclass-templates';
import type { DrawableImageData, DrawableObjectData } from '../../interfaces/drawable-data.interface';
import { photoPlacementRect } from '../../image-positioning/fit-rect';
import {
  buildImagePassthroughMeta,
  carriedBoxFields,
  imagePassthroughBox,
  imagePassthroughParams,
  mergeBoxPassthroughMeta,
} from '../passthrough';
import { assignedPhotoFromImageAlbum, imageAlbumFromAssignedPhoto } from './assigned-photo';
import { buildDrawableEffects, mergeDrawableEffects } from './effects';
import { rectFromBox, rectToBox } from './rect';
import { mergeRotation } from './rotation';
import type { BoxDecoder, BoxEncoder, NormalizerContext } from './codec-contract';

const KNOWN_FRAME_ROLES = new Set<string>(Object.values(ImageFrameRole));

/**
 * THE law "the model knows this frame role by name" — the only instance in the repo, exported because
 * a v1-native predicate reads `params.frameRole` RAW off the persist and must judge it exactly the way
 * decode does. Anything outside `ImageFrameRole` is not a role: decode drops it, so a v0-side reader
 * (`!drawable.frameRole`) sees no role at all, and a boxed reader that trusted raw truthiness instead
 * would keep a hole the drawable branch cuts.
 */
export function isKnownImageFrameRole(value: unknown): value is ImageFrameRole {
  return typeof value === 'string' && KNOWN_FRAME_ROLES.has(value);
}

/** Роль — необязательное поле: отсутствие не warn; нераспознанное значение снимается, рамка судится группой. */
function frameRoleParams(value: string | undefined, ctx: NormalizerContext): { frameRole?: ImageFrameRole } {
  if (value === undefined) return {};
  if (isKnownImageFrameRole(value)) return { frameRole: value };
  ctx.warn(`params.frameRole unknown value "${value}", dropping — frame falls back to its group role`);
  return {};
}

export const decode: BoxDecoder<CustomImageTemplateBox, DrawableImageData> = (box, ctx) => {
  if (box.params.previewThumbHash !== undefined) {
    ctx.drop('previewThumbHash removed in Phase 3 — use versions.thumbHash');
  }

  if (box.params.previewPixelData !== undefined) {
    ctx.drop('previewPixelData removed in Phase 3 — persisted asset lives in versions');
  }

  const container = rectFromBox(box.rect, ctx);
  const hasAssignedPhoto = box.params.assignedPhoto != null;
  const placementRect = box.params.photoPlacementRect;

  if (placementRect && !hasAssignedPhoto) {
    ctx.warn('orphan-photo-placement-rect: photoPlacementRect present without assignedPhoto');
  }

  // Кадр берётся с персиста только при родной пропорции фото; иначе (и без кадра) — cover по родному размеру.
  const image = photoPlacementRect(
    hasAssignedPhoto ? placementRect : undefined,
    box.params.assignedPhoto?.nativeWidth,
    box.params.assignedPhoto?.nativeHeight,
    container,
    box.params.fit,
  );

  return {
    id: box.id,
    type: 'image',
    ...carriedBoxFields(box),
    order: 0, // dispatcher проставляет реальный order по индексу в collage
    canResize: false,
    opacity: box.opacity,
    blendMode: box.blendMode,
    visible: box.visible,
    locked: box.locked,
    rotation: box.rotation,
    effects: buildDrawableEffects(box.effects),
    image: box.params.assignedPhoto ? imageAlbumFromAssignedPhoto(box.params.assignedPhoto, message => ctx.warn(message)) : null,
    ...(box.params.disputedPhoto && {
      disputedPhoto: imageAlbumFromAssignedPhoto(box.params.disputedPhoto, message => ctx.warn(message)),
    }),
    hasFaceOnFold: box.params.hasFaceOnFold ?? false,
    faceOnFoldAccepted: box.params.faceOnFoldAccepted ?? false,
    ...frameRoleParams(box.params.frameRole, ctx),
    fit: box.params.fit,
    flipHorizontal: box.params.flipHorizontal,
    flipVertical: box.params.flipVertical,
    naturalAligns: { container, image },
    maskDataUrl: box.params.maskDataUrl,
    maskRect: box.params.maskRect,
    maskUrl: box.params.maskUrl,
    maskFileId: box.params.maskFileId,
    maskBaseBoxId: box.params.maskBaseBoxId,
    paths: box.params.paths,
    templateMeta: buildImagePassthroughMeta(box),
  };
};

export const encode: BoxEncoder<DrawableImageData, CustomImageTemplateBox> = (drawable, ctx) => {
  // `== null`: a legacy album can carry a photo frame with no `image` key at all, and `undefined`
  // slipping past a `=== null` check reached assignedPhotoFromImageAlbum as a raw TypeError.
  const image = drawable.image ?? null;
  if (image === null && drawable.naturalAligns.image.angle !== 0) {
    // An empty frame keeps no photoPlacementRect at all, so left/top/width/height are already dropped
    // unconditionally — failing on the fifth number of the same rect would only make such a book
    // permanently unsavable as v1 (persist-codec falls back to legacy) with nothing to rotate anyway.
    ctx.warn('naturalAligns.image.angle dropped — image is null so photoPlacementRect cannot be written');
  }

  const photoPlacementRect =
    image !== null
      ? {
          left: drawable.naturalAligns.image.left,
          top: drawable.naturalAligns.image.top,
          width: drawable.naturalAligns.image.width,
          height: drawable.naturalAligns.image.height,
          ...(drawable.naturalAligns.image.angle !== 0 && { angle: drawable.naturalAligns.image.angle }),
        }
      : undefined;

  const box: CustomImageTemplateBox = {
    type: BoxType.IMAGE,
    id: drawable.id,
    ...carriedBoxFields(drawable),
    ...imagePassthroughBox(drawable.templateMeta),
    rect: rectToBox(drawable.naturalAligns.container, ctx),
    rotation: mergeRotation(drawable, ctx),
    opacity: drawable.opacity,
    blendMode: drawable.blendMode,
    visible: drawable.visible,
    locked: drawable.locked,
    effects: mergeDrawableEffects(
      drawable.effects,
      drawable.templateMeta?.rawEffects,
      drawable.layerStyleRendering === 'live' ? 'typed' : undefined,
    ),
    params: {
      ...frameRoleParams(drawable.frameRole, ctx),
      maskDataUrl: drawable.maskDataUrl,
      maskRect: drawable.maskRect,
      maskUrl: drawable.maskUrl,
      maskFileId: drawable.maskFileId,
      maskBaseBoxId: drawable.maskBaseBoxId,
      paths: drawable.paths,
      fit: drawable.fit,
      flipHorizontal: drawable.flipHorizontal,
      flipVertical: drawable.flipVertical,
      assignedPhoto: image !== null ? assignedPhotoFromImageAlbum(image) : null,
      // Written only while the frame remembers a disputed photo — the vast majority of frames never do.
      // Same AssignedPhoto shape, URL included, by design: the pure restore op re-seats without a fetch.
      ...(drawable.disputedPhoto && { disputedPhoto: assignedPhotoFromImageAlbum(drawable.disputedPhoto) }),
      photoPlacementRect,
      // Written only when true: an absent flag and an explicit `false` render identically, and the vast
      // majority of frames carry neither — an explicit false on every box would be pure JSON churn.
      ...(drawable.hasFaceOnFold && { hasFaceOnFold: true }),
      ...(drawable.faceOnFoldAccepted && { faceOnFoldAccepted: true }),
      ...imagePassthroughParams(drawable.templateMeta),
    },
  };

  return mergeBoxPassthroughMeta(box, drawable.templateMeta);
};

/** One box decodes to an image + a sibling `${id}__frame` drawable — decode() returns only one, dispatcher calls this too. */
export function decodeFrameSibling(box: CustomImageTemplateBox, image: DrawableImageData): DrawableObjectData | undefined {
  const { frameUrl, frameFileId, frameWindow } = box.params;
  if (!frameUrl || !frameWindow || !(frameWindow.width > 0) || !(frameWindow.height > 0)) return undefined;

  const container = image.naturalAligns.container;
  const width = container.width / frameWindow.width;
  const height = container.height / frameWindow.height;

  return {
    id: `${box.id}__frame`,
    // Keyed off the image's OWN template link when it has one, so a frame minted here matches the shape
    // product generation writes (`shared-box-converters::generateDrawableImageFrame`) and the pairing in
    // `normalize.ts::isFrameSiblingOf` still finds it after the next round-trip.
    idFromTemplate: `${box.idFromTemplate ?? box.id}__frame`,
    type: 'object',
    order: 0,
    canResize: false,
    opacity: box.opacity,
    visible: box.visible,
    // The frame is a sibling minted from the same box, so it inherits the box's padlock — a locked photo
    // must not leave a draggable frame behind it. `encodeFrameSibling` reads only geometry and the asset,
    // so the box-level flag written by encode() stays the single source on the way back.
    locked: box.locked,
    rotation: box.rotation,
    data: { url: frameUrl, fileId: frameFileId },
    naturalAligns: {
      container: {
        left: container.left - frameWindow.x * width,
        top: container.top - frameWindow.y * height,
        width,
        height,
        angle: container.angle,
      },
    },
  };
}

/** Inverse of `decodeFrameSibling` — dispatcher passes the neighbor it found back in. */
export function encodeFrameSibling(
  image: DrawableImageData,
  frame: DrawableObjectData | undefined,
): Pick<ImageBoxParams, 'frameUrl' | 'frameFileId' | 'frameWindow'> {
  if (!frame) return {};
  const container = image.naturalAligns.container;
  const frameRect = frame.naturalAligns.container;
  if (frameRect.width <= 0 || frameRect.height <= 0) return {};

  return {
    frameUrl: frame.data?.url,
    frameFileId: frame.data?.fileId,
    frameWindow: {
      x: (container.left - frameRect.left) / frameRect.width,
      y: (container.top - frameRect.top) / frameRect.height,
      width: container.width / frameRect.width,
      height: container.height / frameRect.height,
    },
  };
}
